import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Store, UtensilsCrossed, Monitor, QrCode, Bot,
  ArrowRight, ArrowLeft, X, Sparkles, Printer, Globe, Truck
} from "lucide-react";

const STORAGE_KEY = "kaiso_tour_completed";

const STEPS = {
  pt: [
    {
      icon: Store,
      color: "#10b981",
      title: "Configure seu restaurante",
      desc: "Adicione nome, logo, endereco e horarios de funcionamento.",
      action: "/settings",
      actionLabel: "Ir para Configuracoes",
      tip: "O cardapio so fica visivel apos preencher os dados basicos."
    },
    {
      icon: UtensilsCrossed,
      color: "#6366f1",
      title: "Monte seu cardapio",
      desc: "Crie categorias, adicione produtos com fotos e precos. A IA pode criar tudo automaticamente!",
      action: "/menu",
      actionLabel: "Ir para Cardapio",
      tip: "Use a IA para gerar descricoes e fotos profissionais."
    },
    {
      icon: Printer,
      color: "#f59e0b",
      title: "Configure a impressora",
      desc: "Instale o QZ Tray no computador e conecte sua impressora termica para imprimir recibos direto.",
      action: "/settings?tab=impresoras",
      actionLabel: "Configurar Impressora",
      tip: "Sem QZ Tray, a impressao abre o dialogo do navegador."
    },
    {
      icon: Monitor,
      color: "#ef4444",
      title: "Comece a vender no POS",
      desc: "Use o caixa para registrar vendas, dividir contas, emitir faturas e imprimir recibos.",
      action: "/pos",
      actionLabel: "Abrir POS",
      tip: "O POS funciona em qualquer tela: computador, tablet ou celular."
    },
    {
      icon: QrCode,
      color: "#8b5cf6",
      title: "Ative o QR code nas mesas",
      desc: "Cada mesa tem um QR unico. O cliente escaneia e faz o pedido pelo celular. Vai direto pra cozinha!",
      action: "/tables",
      actionLabel: "Gerenciar Mesas",
      tip: "Imprima os QR codes e coloque nas mesas."
    },
    {
      icon: Bot,
      color: "#CDA34F",
      title: "Agente IA 24/7",
      desc: "Seu assistente inteligente esta no menu lateral. Ele responde duvidas, ajuda com configuracoes e tambem atende seus clientes no cardapio digital!",
      action: "/ai-assistant",
      actionLabel: "Falar com a IA",
      tip: "Avise seus clientes para clicarem no chat IA no cardapio!"
    }
  ],
  es: [
    {
      icon: Store, color: "#10b981",
      title: "Configura tu restaurante",
      desc: "Agrega nombre, logo, direccion y horarios de funcionamiento.",
      action: "/settings", actionLabel: "Ir a Configuracion",
      tip: "El menu solo es visible tras completar los datos basicos."
    },
    {
      icon: UtensilsCrossed, color: "#6366f1",
      title: "Monta tu menu",
      desc: "Crea categorias, agrega productos con fotos y precios. La IA puede crear todo automaticamente!",
      action: "/menu", actionLabel: "Ir al Menu",
      tip: "Usa la IA para generar descripciones y fotos profesionales."
    },
    {
      icon: Printer, color: "#f59e0b",
      title: "Configura la impresora",
      desc: "Instala QZ Tray en el ordenador y conecta tu impresora termica para imprimir recibos directamente.",
      action: "/settings?tab=impresoras", actionLabel: "Configurar Impresora",
      tip: "Sin QZ Tray, la impresion abre el dialogo del navegador."
    },
    {
      icon: Monitor, color: "#ef4444",
      title: "Empieza a vender en el POS",
      desc: "Usa la caja para registrar ventas, dividir cuentas, emitir facturas e imprimir recibos.",
      action: "/pos", actionLabel: "Abrir POS",
      tip: "El POS funciona en cualquier pantalla: ordenador, tablet o movil."
    },
    {
      icon: QrCode, color: "#8b5cf6",
      title: "Activa el QR en las mesas",
      desc: "Cada mesa tiene un QR unico. El cliente escanea y hace el pedido desde el movil. Va directo a la cocina!",
      action: "/tables", actionLabel: "Gestionar Mesas",
      tip: "Imprime los QR y colocales en las mesas."
    },
    {
      icon: Bot, color: "#CDA34F",
      title: "Agente IA 24/7",
      desc: "Tu asistente inteligente esta en el menu lateral. Responde dudas, ayuda con configuraciones y tambien atiende a tus clientes en el menu digital!",
      action: "/ai-assistant", actionLabel: "Hablar con la IA",
      tip: "Avisa a tus clientes que pueden usar el chat IA en el menu!"
    }
  ],
  en: [
    {
      icon: Store, color: "#10b981",
      title: "Set up your restaurant",
      desc: "Add name, logo, address and business hours.",
      action: "/settings", actionLabel: "Go to Settings",
      tip: "The menu is only visible after filling in the basic info."
    },
    {
      icon: UtensilsCrossed, color: "#6366f1",
      title: "Build your menu",
      desc: "Create categories, add products with photos and prices. AI can create everything automatically!",
      action: "/menu", actionLabel: "Go to Menu",
      tip: "Use AI to generate descriptions and professional photos."
    },
    {
      icon: Printer, color: "#f59e0b",
      title: "Set up the printer",
      desc: "Install QZ Tray on your computer and connect your thermal printer for direct receipt printing.",
      action: "/settings?tab=impresoras", actionLabel: "Set Up Printer",
      tip: "Without QZ Tray, printing opens the browser dialog."
    },
    {
      icon: Monitor, color: "#ef4444",
      title: "Start selling with POS",
      desc: "Use the register to make sales, split bills, issue invoices and print receipts.",
      action: "/pos", actionLabel: "Open POS",
      tip: "POS works on any screen: computer, tablet or phone."
    },
    {
      icon: QrCode, color: "#8b5cf6",
      title: "Activate table QR codes",
      desc: "Each table has a unique QR. Customer scans and orders from their phone. Goes straight to the kitchen!",
      action: "/tables", actionLabel: "Manage Tables",
      tip: "Print the QR codes and place them on tables."
    },
    {
      icon: Bot, color: "#CDA34F",
      title: "24/7 AI Agent",
      desc: "Your smart assistant is in the sidebar. It answers questions, helps with settings and also serves your customers on the digital menu!",
      action: "/ai-assistant", actionLabel: "Talk to AI",
      tip: "Tell your customers to click the AI chat in the menu!"
    }
  ]
};

export function OnboardingTour({ lang = "pt", isRestaurantActivated = false }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  const steps = STEPS[lang] || STEPS.pt;

  useEffect(() => {
    // Don't show tour if restaurant is already activated/onboarded
    if (isRestaurantActivated) {
      return;
    }
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isRestaurantActivated]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  const goStep = (dir) => {
    setAnimating(true);
    setTimeout(() => {
      setStep(prev => prev + dir);
      setAnimating(false);
    }, 200);
  };

  const goToAction = () => {
    close();
    navigate(steps[step].action);
  };

  if (!visible) return null;

  const s = steps[step];
  const Icon = s.icon;
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="onboarding-tour">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />

      {/* Card */}
      <div className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transition-all duration-200 ${animating ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}>
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-1 transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: s.color }} />
        </div>

        {/* Close */}
        <button onClick={close} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-gray-100 transition z-10" data-testid="tour-close">
          <X className="w-4 h-4 text-gray-400" />
        </button>

        {/* Header with color accent */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#CDA34F]" />
            <span className="text-[10px] font-black text-[#CDA34F] uppercase tracking-widest">
              {lang === "en" ? "Quick Start" : lang === "es" ? "Inicio Rapido" : "Inicio Rapido"} - {step + 1}/{steps.length}
            </span>
          </div>

          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg" style={{ backgroundColor: `${s.color}15`, boxShadow: `0 8px 32px ${s.color}20` }}>
            <Icon className="w-8 h-8" style={{ color: s.color }} />
          </div>

          {/* Content */}
          <h2 className="text-xl font-extrabold text-gray-900 mb-2" data-testid="tour-step-title">{s.title}</h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">{s.desc}</p>

          {/* Tip */}
          <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
            <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
              <span className="text-sm">💡</span> {s.tip}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? "w-6" : ""}`}
                style={{ backgroundColor: i === step ? s.color : "#e5e7eb" }} />
            ))}
          </div>

          <div className="flex gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={() => goStep(-1)} className="h-9 px-3" data-testid="tour-prev">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" onClick={goToAction} className="h-9 px-4 text-white font-bold text-xs" style={{ backgroundColor: s.color }} data-testid="tour-action">
              {s.actionLabel}
            </Button>
            {!isLast && (
              <Button variant="outline" size="sm" onClick={() => goStep(1)} className="h-9 px-3" data-testid="tour-next">
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            {isLast && (
              <Button size="sm" onClick={close} className="h-9 px-4 bg-gray-900 text-white font-bold text-xs" data-testid="tour-finish">
                {lang === "en" ? "Got it!" : lang === "es" ? "Entendido!" : "Entendi!"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
